import React, {createContext, useEffect, useReducer} from 'react';
import SchemaContextReducer, {SchemaContextActionType} from './SchemaContextReducer';
import SomlService from '../../../services/soml-service';
import {SchemaStatistic} from "../../../types/SchemaStatistics";
import {Schema, SearchableProperty} from '../../../types/Schema';
import {RbacPermissions} from "../../../types/Rbac";
import * as RbacUtils from "../../../utils/rbac-utils";
import {GraphQlSchema} from "../../../types/GraphQlSchema";
import GraphQLService from "../../../services/graphql-service";
import Notification from "../../../common/Notification";
import {DataValidationReport, ValidationStatus} from "../../../services/models/validation-model";

const DEFAULT_STATS_LIMIT = 7;
// In milliseconds
const DEFAULT_POLL_PERIOD = 5000;

export type SchemaState = {
  loading: boolean;
  schema: string;
  graphQlSchema?: GraphQlSchema;
  schemaId: string;
  isBound: boolean;
  stats: Array<SchemaStatistic>;
  error: any;
  permissions: RbacPermissions;
  searchableProperties: Map<string, Array<SearchableProperty>>;
  defaultAnalyzer: string;
  isDataValidationRunning: boolean;
  dataValidationReport: DataValidationReport | undefined;
  validateData: (schemaId: string) => Promise<void>;
  runDataValidation: (schemaId: string) => Promise<void>;
  stopDataValidation: (schemaId: string) => Promise<void>;
  pollDataValidationStatus: (schemaId: string) => void;
  stopValidationStatusPolling: () => void;
}

// Initial state
const initialState: SchemaState = {
  loading: false,
  schema: '',
  graphQlSchema: undefined,
  schemaId: '',
  isBound: false,
  stats: [],
  error: '',
  permissions: RbacUtils.NO_PERMISSIONS,
  searchableProperties: new Map<string, Array<SearchableProperty>>(),
  defaultAnalyzer: '',
  isDataValidationRunning: false,
  dataValidationReport: undefined,
  validateData: (schemaId: string) => {
    return Promise.resolve();
  },
  runDataValidation: (schemaId: string) => {
    return Promise.resolve();
  },
  stopDataValidation: (schemaId: string) => {
    return Promise.resolve();
  },
  pollDataValidationStatus: (schemaId: string) => {
    return;
  },
  stopValidationStatusPolling: () => {
    return;
  }
};

export const SchemaContext = createContext(initialState);

let pollerInterval: any;

/**
 * Component which preloads a schema by schema id or finds an active schema if an id is not provided.
 */
export const SchemaContextProvider = (props: any) => {
  const [state, dispatch] = useReducer(SchemaContextReducer, initialState);

  const handleError = (error: any) => {
    console.error('error: ', error);
    let msg = error.message;
    if (error.response && error.response.data) {
      msg = error.response.data;
    }
    dispatch({
      type: SchemaContextActionType.ADD_ERROR,
      payload: {
        error: msg,
        loading: false
      }
    });
  };

  const validateData = (schemaId: string) => {
    return SomlService.getDataValidationStatus(schemaId).then((report) => {
      const isRunning = report.status === ValidationStatus.RUNNING;
      if (!isRunning) {
        runDataValidation(schemaId);
        updateDataValidationState(true, report);
      }
    }).catch((error) => handleMissingValidationReportOnStartError(error, schemaId));
  }

  const runDataValidation = (schemaId: string) => {
    return SomlService.validateData(schemaId).then(() => {
      Notification.success({message: 'Data validation started successfully.'});
    }).catch(handleValidationStartError);
  }

  const stopDataValidation = (schemaId: string) => {
    return SomlService.cancelDataValidation(schemaId).then((report: DataValidationReport) => {
      Notification.success({message: 'Data validation stopped successfully.'});
      updateDataValidationState(false, report);
    }).catch((error) => {
      console.log('Error during validation stop', error);
    });
  }

  const handleMissingValidationReportOnStartError = (error: { code: string }, schemaId: string): void  => {
    // validation has never run for the schema
    if (error.code && error.code === '4040001') {
      updateDataValidationState(true);
      runDataValidation(schemaId);
    }
  }

  const updateDataValidationState = (isRunning: boolean, report?: DataValidationReport): void => {
    dispatch({
      type: SchemaContextActionType.UPDATE_DATA_VALIDATION_RUNNING,
      payload: {
        dataValidationReport: report,
        isDataValidationRunning: isRunning
      }
    });
  }

  const fetchAndUpdateValidationStatus = (schemaId: string) => {
    SomlService.getDataValidationStatus(schemaId).then((report) => {
      const isRunning = report.status === ValidationStatus.RUNNING;
      updateDataValidationState(isRunning, report);
    }).catch(() => {});
  }

  const pollDataValidationStatus = (schemaId: string): void => {
    if (!pollerInterval) {
      fetchAndUpdateValidationStatus(schemaId);
      pollerInterval = setInterval(() => fetchAndUpdateValidationStatus(schemaId), DEFAULT_POLL_PERIOD);
    }
  }

  const stopValidationStatusPolling = () => {
    clearInterval(pollerInterval);
    pollerInterval = null;
  }

  const handleValidationStartError = (error: string) => {
    Notification.error({message: `Validation was not started due to an error: ${error}`})
    updateDataValidationState(false);
  }

  useEffect(() => {
    const setLoading = (loading: boolean) => {
      dispatch({
        type: SchemaContextActionType.LOADING,
        payload: {loading}
      });
    };

    const processLoadedSchema = (schema: Schema) => {
      dispatch({
        type: SchemaContextActionType.GET_SCHEMA,
        payload: {
          schema: schema.schemaYaml,
          schemaId: schema.id,
          isBound: schema.bound,
          loading: false,
          permissions: schema.permissions,
          defaultAnalyzer: schema.defaultAnalyzer,
          searchableProperties: schema.searchableProperties
        }
      });
    };

    const loadSchemaStatistics = (schema: Schema) => {
      if (!schema.bound) {
        return Promise.resolve();
      }
      setLoading(true);
      return SomlService.getSchemaStatistics(schema.id, DEFAULT_STATS_LIMIT).then(statistics => {
        dispatch({
          type: SchemaContextActionType.GET_STATISTICS,
          payload: {
            stats: statistics,
            loading: false
          }
        });
      });
    };

    const loadGraphQlSchema = () => {
      if (props.loadGraphQlSchema) {
        setLoading(true);
        GraphQLService.executeSchemaIntrospection('federation').then((graphQlSchema: GraphQlSchema) => {
          dispatch({
            type: SchemaContextActionType.GET_GQL_SCHEMA,
            payload: {
              graphQlSchema,
              loading: false
            }
          });
          return graphQlSchema;
        }).catch(handleError);
      }
    };

    const loadSchema = (schemaId: string) => {
      setLoading(true);
      SomlService.getSchema(schemaId).then((schema: Schema) => {
        processLoadedSchema(schema);
        return schema;
      })
        .then(loadSchemaStatistics)
        .catch(handleError);
    };

    const loadActiveSchema = () => {
      setLoading(true);
      SomlService.getAllSchemas().then((schemas: Array<Schema>) => {
        const boundSchema = schemas.find((schema: Schema) => {
          return schema.bound;
        });
        if (!boundSchema) {
          return Promise.reject({message: 'No active schema was found'});
        }
        processLoadedSchema(boundSchema);
        return boundSchema;
      })
        .then(loadSchemaStatistics)
        .then(loadGraphQlSchema)
        .catch(handleError);
    };

    if (props.schemaId) {
      loadSchema(props.schemaId);
    } else {
      loadActiveSchema();
    }
  }, [props.schemaId, props.loadGraphQlSchema]); // The empty array causes this effect to only run on mount

  return (
    <SchemaContext.Provider value={{
      loading: state.loading,
      schema: state.schema,
      graphQlSchema: state.graphQlSchema,
      schemaId: state.schemaId,
      isBound: state.isBound,
      stats: state.stats,
      error: state.error,
      permissions: state.permissions,
      searchableProperties: state.searchableProperties,
      defaultAnalyzer: state.defaultAnalyzer,
      isDataValidationRunning: state.isDataValidationRunning,
      dataValidationReport: state.dataValidationReport,
      validateData,
      runDataValidation,
      stopDataValidation,
      pollDataValidationStatus,
      stopValidationStatusPolling
    }}>
      {props.children}
    </SchemaContext.Provider>
  );
};
